import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dashboardAPI, jobAPI, mentorshipAPI, teamAPI, usersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Trophy, Calendar, Eye, Search } from 'lucide-react';

interface DashboardData {
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    profileCompleted: number;
  };
  profileCompletion: number;
  registrations: Array<{
    _id: string;
    eventId: {
      _id: string;
      title: string;
      startTime: string;
      endTime: string;
      location: string;
      category: string;
    };
    status: string;
    registeredAt: string;
    type: 'event' | 'hackathon';
    paymentId?: string;
    payment?: {
      _id: string;
      razorpayPaymentId: string;
      razorpayOrderId: string;
      receipt: string;
      amount: number;
      createdAt: string;
    };
  }>;
  upcomingEvents: Array<{
    _id: string;
    title: string;
    description: string;
    startTime: string;
    endTime: string;
    location: string;
    category: string;
    capacity: number;
    currentRegistrations: number;
  }>;
  recentActivities: Array<{
    _id: string;
    eventId: {
      _id: string;
      title: string;
      startTime: string;
    };
    status: string;
    registeredAt: string;
  }>;
  statistics: {
    totalRegistrations: number;
    attendedEvents: number;
    upcomingRegistrations: number;
    attendanceRate: number;
  };
}

interface Job {
  _id: string;
  jobId: string;
  title: string;
  companyName: string;
  location: string;
  employmentType: string;
  experienceLevel: string;
  salaryRange?: {
    min: number;
    max: number;
  };
  jobCategory: string;
  numberOfOpenings: number;
  lastDateToApply?: string;
  createdAt: string;
  postedBy: {
    firstName: string;
    lastName: string;
  };
}

interface MentorshipRequest {
  _id: string;
  mentor: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    profileImageUrl?: string;
    primarySkills?: string[];
    secondarySkills?: string[];
    expertise?: {
      technical?: string[];
      mentoring?: string[];
      industries?: string[];
    };
  };
  message: string;
  status: 'pending' | 'accepted' | 'rejected' | 'disconnected';
  createdAt: string;
  respondedAt?: string;
  mentorNotes?: string;
}

interface Team {
  _id: string;
  name: string;
  hackathon: {
    _id: string;
    title: string;
    startDate: string;
    endDate: string;
    status: string;
  };
  leader: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  members: Array<{
    user: {
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
    role: string;
    joinedAt: string;
  }>;
  status: string;
  isApproved: boolean;
  isActive: boolean;
  joinCode: string;
  submission?: {
    title: string;
    description: string;
    githubLink: string;
    demoLink: string;
    submittedAt: string;
  };
  createdAt: string;
}

interface Mentor {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  profileImageUrl?: string;
  bio?: string;
  primarySkills?: string[];
  secondarySkills?: string[];
  expertise?: {
    technical: string[];
    mentoring: string[];
    industries: string[];
  };
  experience?: {
    yearsOfExperience: number;
    currentCompany?: string;
    currentRole?: string;
  };
  availability?: {
    timeSlots: string[];
  };
  mentoringApproach?: string;
  isApproved: boolean;
  createdAt: string;
}

const AspirantDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [recentJobs, setRecentJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Mentorship state
  const [mentorshipRequests, setMentorshipRequests] = useState<MentorshipRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'pending' | 'accepted' | 'all'>('accepted');
  
  // Teams state
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  
  // Mentors state
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [filteredMentors, setFilteredMentors] = useState<Mentor[]>([]);
  const [mentorsLoading, setMentorsLoading] = useState(false);
  const [mentorSearchTerm, setMentorSearchTerm] = useState('');

  useEffect(() => {
    fetchDashboardData();
    fetchTeams();
    fetchMentors();
  }, []);

  useEffect(() => {
    if (user?.role === 'aspirant') {
      fetchMentorshipRequests();
    }
  }, [user]); // Removed activeTab dependency to avoid refetching on tab change

  useEffect(() => {
    filterMentors();
  }, [mentorSearchTerm, mentors]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch dashboard data and recent jobs in parallel
      const [dashboardResponse, jobsResponse] = await Promise.all([
        dashboardAPI.getAspirantDashboard(),
        jobAPI.getJobs({ limit: 5 }) // Get 5 most recent jobs
      ]);
      
      if (dashboardResponse.success && dashboardResponse.data) {
        setDashboardData(dashboardResponse.data);
      } else {
        throw new Error(dashboardResponse.message || 'Failed to fetch dashboard data');
      }
      
      if (jobsResponse.success && jobsResponse.data) {
        setRecentJobs(jobsResponse.data.jobs);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMentorshipRequests = async () => {
    try {
      setRequestsLoading(true);
      console.log('Fetching all mentorship requests for aspirant:', user?.email);
      
      // Always fetch all requests to get proper counts
      const response = await mentorshipAPI.getAspirantRequests('all', 1, 50);
      
      if (response.success && response.data?.requests) {
        setMentorshipRequests(response.data.requests);
        console.log('Set aspirant mentorship requests:', response.data.requests);
      }
    } catch (err: any) {
      console.error('Error fetching mentorship requests:', err);
    } finally {
      setRequestsLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      setTeamsLoading(true);
      const response = await teamAPI.getMyTeams();
      setTeams(response);
    } catch (err: any) {
      console.error('Error fetching teams:', err);
    } finally {
      setTeamsLoading(false);
    }
  };

  const fetchMentors = async () => {
    try {
      setMentorsLoading(true);
      const response = await usersAPI.getMentors();
      
      if (response.success && response.data) {
        setMentors(response.data);
        setFilteredMentors(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching mentors:', err);
    } finally {
      setMentorsLoading(false);
    }
  };

  const filterMentors = () => {
    let filtered = mentors;

    if (mentorSearchTerm) {
      const term = mentorSearchTerm.toLowerCase();
      filtered = filtered.filter(mentor =>
        mentor.firstName.toLowerCase().includes(term) ||
        mentor.lastName.toLowerCase().includes(term) ||
        mentor.bio?.toLowerCase().includes(term) ||
        mentor.experience?.currentCompany?.toLowerCase().includes(term) ||
        mentor.experience?.currentRole?.toLowerCase().includes(term) ||
        mentor.email.toLowerCase().includes(term)
      );
    }

    setFilteredMentors(filtered);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'forming': return 'bg-yellow-100 text-yellow-800';
      case 'pending_approval': return 'bg-orange-100 text-orange-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'submitted': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusDisplayText = (status: string) => {
    switch (status) {
      case 'forming': return 'FORMING';
      case 'pending_approval': return 'PENDING APPROVAL';
      case 'approved': return 'APPROVED';
      case 'active': return 'ACTIVE';
      case 'submitted': return 'SUBMITTED';
      case 'completed': return 'COMPLETED';
      case 'rejected': return 'REJECTED';
      default: return status.toUpperCase();
    }
  };

  const handleDisconnect = async (requestId: string) => {
    try {
      await mentorshipAPI.disconnect(requestId);
      await fetchMentorshipRequests();
    } catch (err: any) {
      console.error('Error disconnecting mentorship:', err);
    }
  };

  const handleDownloadReceipt = async (paymentId: string) => {
    try {
      const response = await fetch(`/api/payments/receipt/${paymentId}`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt-${paymentId}.html`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Failed to download receipt');
      }
    } catch (error) {
      console.error('Error downloading receipt:', error);
    }
  };

  if (loading) {
    return (
      <LoadingSpinner 
        text="Loading Dashboard"
        subtitle="Preparing your career dashboard..."
        size="lg"
      />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="p-6 bg-red-50 rounded-2xl w-20 h-20 mx-auto flex items-center justify-center shadow-lg">
            <svg className="h-10 w-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-secondary">Error Loading Dashboard</h3>
            <p className="text-gray-600 leading-relaxed">{error}</p>
          </div>
          <button 
            onClick={fetchDashboardData}
            className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="p-6 bg-background rounded-2xl w-20 h-20 mx-auto flex items-center justify-center shadow-lg">
            <svg className="h-10 w-10 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-secondary">No Dashboard Data</h3>
            <p className="text-gray-600 leading-relaxed">No dashboard data available at the moment.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-surface shadow-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="space-y-6">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start space-y-4 lg:space-y-0">
              <div className="space-y-4">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-secondary">
                  Aspirant Dashboard
                </h1>
                <p className="text-gray-600 text-lg">Welcome back, {dashboardData.user.firstName}!</p>
                
                {/* Navigation Tabs */}
                <div className="flex flex-wrap gap-3 pt-4">
                  <Link
                    to="/aspirant/profile"
                    className="flex items-center px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-all duration-300"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Profile
                  </Link>
                  <Link
                    to="/jobs"
                    className="flex items-center px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-all duration-300"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6M8 8v10a2 2 0 002 2h4a2 2 0 002-2V8" />
                    </svg>
                    Jobs
                  </Link>
                  <Link
                    to="/events"
                    className="flex items-center px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-all duration-300"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Events
                  </Link>

                  <button
                    onClick={() => {
                      const teamsSection = document.getElementById('my-teams-section');
                      if (teamsSection) {
                        teamsSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="inline-flex items-center px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-all duration-300"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    My Teams
                  </button>
                  <button
                    onClick={() => {
                      const mentorsSection = document.getElementById('find-mentors-section');
                      if (mentorsSection) {
                        mentorsSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="flex items-center px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-all duration-300"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Find Mentors
                  </button>
                  <button
                    onClick={() => {
                      const mentorsSection = document.getElementById('my-mentors-section');
                      if (mentorsSection) {
                        mentorsSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="flex items-center px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-all duration-300"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    My Mentors
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Swapped Layout */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Main Content (Previously Right Column) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Recently Added Jobs */}
            <div className="bg-surface rounded-2xl shadow-sm border border-secondary p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-secondary">Recently Added Jobs</h3>
                <Link
                  to="/jobs"
                  className="text-primary hover:text-primary-dark font-medium text-sm"
                >
                  View All Jobs →
                </Link>
              </div>
              <div className="space-y-4">
                {recentJobs.length > 0 ? (
                  recentJobs.map((job) => (
                    <div key={job._id} className="p-4 border border-secondary rounded-lg hover:bg-primary/10 transition-colors duration-200">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-semibold text-secondary">{job.title}</h4>
                        <span className="px-2 py-1 bg-primary-light text-primary rounded-full text-xs font-medium">
                          {job.employmentType}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-primary mb-3">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-6m-2-9h6m-6 4h6m-2-9V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4m6 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v2m6 4V9" />
                          </svg>
                          {job.companyName}
                        </div>
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {job.location}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 text-xs text-success">
                          <span className="px-2 py-1 bg-primary/10 rounded">{job.experienceLevel}</span>
                          <span>{job.numberOfOpenings} openings</span>
                          {job.lastDateToApply && (
                            <span>Apply by {new Date(job.lastDateToApply).toLocaleDateString()}</span>
                          )}
                        </div>
                        <Link
                          to={`/jobs/${job._id}`}
                          className="px-3 py-1 bg-secondary hover:bg-secondary-light text-white rounded text-sm transition-colors duration-200"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 text-primary/50 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6M8 8v10a2 2 0 002 2h4a2 2 0 002-2V8" />
                    </svg>
                    <p className="text-primary mb-4">No recent jobs available</p>
                    <Link
                      to="/jobs"
                      className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors duration-200"
                    >
                      Browse All Jobs
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Upcoming Events */}
            <div className="bg-surface rounded-2xl shadow-sm border border-border p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-secondary">Upcoming Events</h3>
                <Link
                  to="/events"
                  className="text-primary hover:text-secondary font-medium text-sm"
                >
                  View All →
                </Link>
              </div>
              <div className="space-y-4">
                {dashboardData.upcomingEvents.length > 0 ? (
                  dashboardData.upcomingEvents.map((event) => (
                    <div key={event._id} className="p-4 border border-border rounded-lg hover:bg-secondary/10 transition-colors duration-200">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-semibold text-secondary">{event.title}</h4>
                        <span className="px-2 py-1 bg-primary-light text-blue-800 rounded-full text-xs font-medium capitalize">
                          {event.category}
                        </span>
                      </div>
                      <p className="text-primary text-sm mb-3 line-clamp-2">{event.description}</p>
                      <div className="flex items-center justify-between text-sm text-primary">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {new Date(event.startTime).toLocaleDateString()}
                        </div>
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {event.location}
                        </div>
                      </div>
                      <div className="mt-3 flex justify-between items-center">
                        <span className="text-xs text-blue-500">
                          {event.currentRegistrations}/{event.capacity} registered
                        </span>
                        <Link
                          to={`/events/${event._id}`}
                          className="px-3 py-1 bg-secondary hover:bg-secondary-light text-white rounded text-sm transition-colors duration-200"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 text-blue-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-primary mb-4">No upcoming events</p>
                    <Link
                      to="/events"
                      className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors duration-200"
                    >
                      Browse Events
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activities */}
            <div className="bg-surface rounded-2xl shadow-sm border border-border p-6">
              <h3 className="text-xl font-semibold text-secondary mb-6">Recent Activities</h3>
              <div className="space-y-4">
                {dashboardData.recentActivities.length > 0 ? (
                  dashboardData.recentActivities.map((activity) => (
                    <div key={activity._id} className="flex items-center p-3 bg-secondary/10 rounded-lg">
                      <div className="w-2 h-2 bg-secondary/100 rounded-full mr-3"></div>
                      <div className="flex-1">
                        <p className="text-secondary font-medium">{activity.eventId.title}</p>
                        <p className="text-primary text-sm">
                          {activity.status === 'registered' || activity.status === 'confirmed' ? 'Registered for' : 'Attended'} event on{' '}
                          {new Date(activity.registeredAt).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        activity.status === 'registered' 
                          ? 'bg-orange-100 text-orange-800' 
                          : 'bg-primary-light text-primary'
                      }`}>
                        {activity.status}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 text-blue-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p className="text-primary">No recent activities</p>
                    <p className="text-blue-500 text-sm mt-2">Start by registering for events!</p>
                  </div>
                )}
              </div>
            </div>

            {/* My Registrations */}
            <div className="bg-surface rounded-2xl shadow-sm border border-border p-6">
              <h3 className="text-xl font-semibold text-secondary mb-6">My Registrations</h3>
              <div className="space-y-4">
                {dashboardData.registrations.length > 0 ? (
                  dashboardData.registrations.map((registration) => (
                    <div key={registration._id} className="p-4 border border-border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-semibold text-secondary">{registration.eventId.title}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          registration.status === 'registered' 
                            ? 'bg-primary-light text-blue-800' 
                            : registration.status === 'attended'
                            ? 'bg-primary-light text-primary'
                            : 'bg-gray-100 text-secondary'
                        }`}>
                          {registration.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-primary mb-2">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {new Date(registration.eventId.startTime).toLocaleDateString()}
                        </div>
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {registration.eventId.location}
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-blue-500">
                          Registered on {new Date(registration.registeredAt).toLocaleDateString()}
                        </span>
                        <div className="flex space-x-2">
                          {registration.paymentId && (
                            <button
                              onClick={() => handleDownloadReceipt(registration.paymentId!)}
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm transition-colors duration-200 flex items-center"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Download Receipt
                            </button>
                          )}
                          <Link
                            to={registration.type === 'hackathon' ? `/hackathons/${registration.eventId._id}` : `/events/${registration.eventId._id}`}
                            className="px-3 py-1 bg-secondary hover:bg-secondary-light text-white rounded text-sm transition-colors duration-200"
                          >
                            View {registration.type === 'hackathon' ? 'Hackathon' : 'Event'}
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 text-blue-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p className="text-primary">No registrations yet</p>
                    <p className="text-blue-500 text-sm mt-2">Start exploring events to register!</p>
                  </div>
                )}
              </div>
            </div>

            {/* My Mentors - Mentorship Requests */}
            <div id="my-mentors-section" className="bg-surface rounded-2xl shadow-sm border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-secondary">My Mentors</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setActiveTab('accepted')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === 'accepted'
                        ? 'bg-secondary text-white'
                        : 'bg-secondary/10 text-secondary hover:bg-secondary/20'
                    }`}
                  >
                    Active ({mentorshipRequests.filter(r => r.status === 'accepted').length})
                  </button>
                  <button
                    onClick={() => setActiveTab('pending')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === 'pending'
                        ? 'bg-secondary text-white'
                        : 'bg-secondary/10 text-secondary hover:bg-secondary/20'
                    }`}
                  >
                    Pending ({mentorshipRequests.filter(r => r.status === 'pending').length})
                  </button>
                </div>
              </div>
              
              <div className="space-y-4">
                {requestsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary mx-auto mb-4"></div>
                    <p className="text-secondary">Loading requests...</p>
                  </div>
                ) : mentorshipRequests.filter(r => activeTab === 'all' || r.status === activeTab).length > 0 ? (
                  mentorshipRequests.filter(r => activeTab === 'all' || r.status === activeTab).map((request) => (
                    <div key={request._id} className="p-4 border border-border rounded-lg hover:bg-secondary/10 transition-colors duration-200">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                            {request.mentor.profileImageUrl ? (
                              <img
                                src={request.mentor.profileImageUrl}
                                alt={`${request.mentor.firstName} ${request.mentor.lastName}`}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-white text-sm font-bold">
                                {request.mentor.firstName[0]}{request.mentor.lastName[0]}
                              </span>
                            )}
                          </div>
                          <div>
                            <h4 className="font-semibold text-secondary">
                              {request.mentor.firstName} {request.mentor.lastName}
                            </h4>
                            <p className="text-xs text-secondary">{request.mentor.email}</p>
                          </div>
                        </div>
                        <Badge 
                          className={`${
                            request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            request.status === 'accepted' ? 'bg-primary-light text-primary' :
                            request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-secondary'
                          }`}
                        >
                          {request.status}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                        Your message: {request.message}
                      </p>
                      
                      {request.mentor.primarySkills && request.mentor.primarySkills.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {request.mentor.primarySkills.slice(0, 3).map((skill, index) => (
                            <span key={index} className="px-2 py-1 bg-secondary/10 text-purple-800 rounded text-xs">
                              {skill}
                            </span>
                          ))}
                          {request.mentor.expertise?.technical && request.mentor.expertise.technical.slice(0, 2).map((skill, index) => (
                            <span key={`tech-${index}`} className="px-2 py-1 bg-primary-light text-blue-800 rounded text-xs">
                              {skill}
                            </span>
                          ))}
                          {(request.mentor.primarySkills.length + (request.mentor.expertise?.technical?.length || 0)) > 5 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                              +{(request.mentor.primarySkills.length + (request.mentor.expertise?.technical?.length || 0)) - 5}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between text-xs text-purple-500 mb-3">
                        <span>Requested on {new Date(request.createdAt).toLocaleDateString()}</span>
                        {request.respondedAt && (
                          <span>Responded on {new Date(request.respondedAt).toLocaleDateString()}</span>
                        )}
                      </div>
                      
                      {request.mentorNotes && (
                        <div className="p-3 bg-background rounded-lg mb-3">
                          <p className="text-sm text-gray-700 font-medium">Mentor Notes:</p>
                          <p className="text-sm text-gray-600">{request.mentorNotes}</p>
                        </div>
                      )}
                      
                      <div className="flex space-x-2">
                        {request.status === 'accepted' && (
                          <Button
                            onClick={() => handleDisconnect(request._id)}
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            Disconnect
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 text-purple-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <p className="text-secondary mb-4">
                      {activeTab === 'pending' ? 'No pending requests' : 'No active mentors'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {activeTab === 'pending' 
                        ? 'Your mentorship requests will appear here'
                        : 'Your accepted mentorships will be shown here'
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Profile & Stats (Previously Left Column) */}
          <div className="lg:col-span-1 space-y-8">
            {/* Profile Completion */}
            <div className="bg-surface border-border shadow-lg rounded-2xl overflow-hidden">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 px-8 py-6">
                <h3 className="text-xl font-bold text-secondary">Profile Completion</h3>
              </div>
              <div className="p-8 space-y-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">Progress</span>
                    <span className="text-2xl font-bold text-primary">{dashboardData.profileCompletion}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-primary h-3 rounded-full transition-all duration-500"
                      style={{ width: `${dashboardData.profileCompletion}%` }}
                    ></div>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Complete your profile to increase your visibility to mentors and opportunities.
                </p>
                <Link
                  to="/aspirant/profile"
                  className="w-full bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center justify-center"
                >
                  Complete Profile
                </Link>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-surface border-border shadow-lg rounded-2xl overflow-hidden">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 px-8 py-6">
                <h3 className="text-xl font-bold text-secondary">Your Stats</h3>
              </div>
              <div className="p-8 space-y-6">
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <span className="text-gray-700 font-medium">Total Registrations</span>
                  <span className="text-3xl font-bold text-primary">{dashboardData.statistics.totalRegistrations}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <span className="text-gray-700 font-medium">Events Attended</span>
                  <span className="text-3xl font-bold text-primary">{dashboardData.statistics.attendedEvents}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <span className="text-gray-700 font-medium">Upcoming Events</span>
                  <span className="text-3xl font-bold text-primary">{dashboardData.statistics.upcomingRegistrations}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <span className="text-gray-700 font-medium">Attendance Rate</span>
                  <span className="text-3xl font-bold text-secondary">{dashboardData.statistics.attendanceRate}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* My Teams Section */}
        <div id="my-teams-section" className="mt-12">
          <div className="bg-surface border-border shadow-lg rounded-2xl overflow-hidden">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 px-8 py-6">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-secondary">My Teams</h3>
                <Link to="/teams/create">
                  <Button className="bg-primary hover:bg-primary-dark text-white">
                    Create New Team
                  </Button>
                </Link>
              </div>
            </div>
            <div className="p-8">
              {teamsLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : teams.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-xl font-medium text-gray-900 mb-2">No teams yet</h4>
                  <p className="text-gray-600 mb-4">
                    You haven't joined any hackathon teams yet. Start by creating a team or joining an existing one.
                  </p>
                  <div className="space-x-4">
                    <Link to="/teams/create">
                      <Button className="bg-primary hover:bg-primary-dark text-white">Create Team</Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {teams.slice(0, 4).map((team) => (
                    <div key={team._id} className="bg-background border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-gray-900 mb-2">{team.name}</h4>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={`${getStatusColor(team.status)} font-semibold text-sm px-3 py-1`}>
                              {getStatusDisplayText(team.status)}
                            </Badge>
                            {/* Only show isActive badge if it provides different information than status */}
                            {(team.status !== 'active' || !team.isActive) && (
                              <Badge className={`${team.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'} font-semibold text-sm px-3 py-1`}>
                                {team.isActive ? 'ACTIVE' : 'INACTIVE'}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Trophy className="h-6 w-6 text-yellow-500" />
                      </div>
                      
                      <div className="space-y-3 mb-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="h-4 w-4 mr-2" />
                          <span>{team.hackathon.title}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Users className="h-4 w-4 mr-2" />
                          <span>{team.members.length} member{team.members.length !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      
                      <Link
                        to={`/teams/${team._id}`}
                        className="inline-flex items-center px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg text-sm font-medium transition-all duration-300"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </Link>
                    </div>
                  ))}
                </div>
              )}
              
              {teams.length > 4 && (
                <div className="mt-6 text-center">
                  <Link to="/teams/my-teams">
                    <Button variant="outline" className="text-primary border-primary hover:bg-primary hover:text-white">
                      View All Teams ({teams.length})
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Find Mentors Section */}
        <div id="find-mentors-section" className="mt-12">
          <div className="bg-surface border-border shadow-lg rounded-2xl overflow-hidden">
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 px-8 py-6">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-secondary">Find Mentors</h3>
                <Link to="/mentors">
                  <Button className="bg-primary hover:bg-primary-dark text-white">
                    View All Mentors
                  </Button>
                </Link>
              </div>
            </div>
            <div className="p-8">
              {/* Search Bar */}
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search mentors by name, company, or skills..."
                    value={mentorSearchTerm}
                    onChange={(e) => setMentorSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              {mentorsLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : filteredMentors.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-xl font-medium text-gray-900 mb-2">
                    {mentorSearchTerm ? 'No mentors found' : 'No mentors available'}
                  </h4>
                  <p className="text-gray-600 mb-4">
                    {mentorSearchTerm 
                      ? 'Try adjusting your search terms to find mentors.'
                      : 'There are currently no approved mentors available.'
                    }
                  </p>
                  {mentorSearchTerm && (
                    <Button 
                      variant="outline" 
                      onClick={() => setMentorSearchTerm('')}
                      className="text-primary border-primary hover:bg-primary hover:text-white"
                    >
                      Clear Search
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {filteredMentors.slice(0, 4).map((mentor) => (
                    <div key={mentor._id} className="bg-background border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start space-x-4 mb-4">
                        <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                          {mentor.profileImageUrl ? (
                            <img 
                              src={mentor.profileImageUrl} 
                              alt={`${mentor.firstName} ${mentor.lastName}`}
                              className="w-16 h-16 rounded-full object-cover"
                            />
                          ) : (
                            <Users className="w-8 h-8 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-lg font-bold text-gray-900 mb-1">
                            {mentor.firstName} {mentor.lastName}
                          </h4>
                          {mentor.experience && (
                            <p className="text-sm text-gray-600 mb-2">
                              {mentor.experience.currentRole} 
                              {mentor.experience.currentCompany && ` at ${mentor.experience.currentCompany}`}
                            </p>
                          )}
                          {mentor.bio && (
                            <p className="text-sm text-gray-700 line-clamp-2">{mentor.bio}</p>
                          )}
                        </div>
                      </div>
                      
                      {mentor.expertise?.technical && mentor.expertise.technical.length > 0 && (
                        <div className="mb-4">
                          <div className="flex flex-wrap gap-2">
                            {mentor.expertise.technical.slice(0, 3).map((skill, index) => (
                              <Badge key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1">
                                {skill}
                              </Badge>
                            ))}
                            {mentor.expertise.technical.length > 3 && (
                              <Badge className="bg-gray-100 text-gray-800 text-xs px-2 py-1">
                                +{mentor.expertise.technical.length - 3} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {mentor.experience?.yearsOfExperience} years experience
                        </span>
                        <Link
                          to="/mentors"
                          className="inline-flex items-center px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg text-sm font-medium transition-all duration-300"
                        >
                          Connect
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {filteredMentors.length > 4 && (
                <div className="mt-6 text-center">
                  <Link to="/mentors">
                    <Button variant="outline" className="text-primary border-primary hover:bg-primary hover:text-white">
                      View All Mentors ({filteredMentors.length})
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AspirantDashboard; 